import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// project imports
import NavItem from '../NavItem';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useAuthentication } from '../../../../../utils/useAuthentication';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const data = useAuthentication();
  const user = data?.user?.account_data;

  let user_admin = user?.admin;
  let user_doctor = user?.doctor;
  let user_clinic = user?.clinic;

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
    if (!menu?.url && menu?.children?.length > 0 && menu?.id !== 'authentication') {
      navigate(menu?.children?.[0]?.url);
    }
    if (menu?.url) {
      navigate(menu?.url);
    }
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child?.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    setOpen(false);
    setSelected(null);
    if (menu.children) {
      menu.children?.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu?.children?.map((item) => {
    if (
      (menu?.permission?.includes('ADMIN') && user_admin) ||
      (menu?.permission?.includes('CLINIC') && user_clinic && !user_admin) ||
      (menu?.permission?.includes('DOCTOR') && user_doctor && !user_admin)
    ) {
      switch (item.type) {
        case 'collapse':
          return <NavCollapse key={item.id} menu={item} level={level + 1} />;
        case 'divider':
          return <hr key={menu?.id} color={'#f8f8f8'} />;
        case 'item':
          return <NavItem key={item.id} item={item} level={level + 1} />;
        default:
          return (
            <Typography key={item.id} variant="h6" color="error" align="center">
              Menu Items Error
            </Typography>
          );
      }
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon strokeWidth={1.67} size="1.3rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: 0,
          alignItems: 'flex-start',
          backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
          py: level > 1 ? 1 : 1.25,
          pl: `${level * 24}px`,
          '&:after':
            selected === menu.id
              ? {
                  content: "''",
                  position: 'absolute',
                  left: '5px',
                  top: 0,
                  height: '100%',
                  width: '3px',
                  opacity: 1,
                  background: '#FDF2B5'
                }
              : {}
        }}
        // selected={selected === menu.id}
        onClick={handleClick}
      >
        <ListItemIcon
          sx={{
            my: 'auto',
            minWidth: !menu.icon ? 18 : 36,
            color: selected === menu.id ? '#FFFFFF' : '#60626E'
          }}
        >
          {menuIcon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={selected === menu.id ? 'h5' : 'body1'}
              sx={{ my: 'auto' }}
              color={selected === menu.id ? '#FDF2B5' : '#A4A6B3'}
              fontFamily={'Oxygen'}
              fontWeight={'700'}
              letterSpacing={0.25}
              fontSize={'16px'}
            >
              {menu.title}
            </Typography>
          }
          secondary={
            menu.caption && (
              <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                {menu.caption}
              </Typography>
            )
          }
        />
        {menu?.children?.length > 0 && (
          <>
            {open ? (
              <IconChevronUp stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
            ) : (
              <IconChevronDown stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
            )}
          </>
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            position: 'relative',
            '&:after': {
              content: "''",
              position: 'absolute',
              left: '5px',
              top: 0,
              height: '100%',
              width: '3px',
              opacity: 1,
              background: '#FDF2B5'
            }
          }}
        >
          {menus}
        </List>
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
